import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import api from "@/lib/api";
import { fmtTime, Loading, ProStatusBadge, EmptyState } from "@/components/common";

const MONTHS = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const WEEKDAYS = ["L", "M", "M", "G", "V", "S", "D"];

const dayKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function ProCalendar({ onClose }) {
  const [items, setItems] = useState(null);
  const today = new Date();
  const [cursor, setCursor] = useState({ year: today.getFullYear(), month: today.getMonth() });
  const [selected, setSelected] = useState(dayKey(today));

  useEffect(() => {
    api.get("/pro/calendar").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const byDay = useMemo(() => {
    const map = {};
    (items || []).forEach((i) => {
      if (!i.scheduled_date) return;
      const k = dayKey(new Date(i.scheduled_date));
      (map[k] = map[k] || []).push(i);
    });
    return map;
  }, [items]);

  const cells = useMemo(() => {
    const first = new Date(cursor.year, cursor.month, 1);
    const offset = (first.getDay() + 6) % 7;
    const days = new Date(cursor.year, cursor.month + 1, 0).getDate();
    const list = [];
    for (let i = 0; i < offset; i++) list.push(null);
    for (let d = 1; d <= days; d++) list.push(dayKey(new Date(cursor.year, cursor.month, d)));
    return list;
  }, [cursor]);

  const move = (delta) => {
    const d = new Date(cursor.year, cursor.month + delta, 1);
    setCursor({ year: d.getFullYear(), month: d.getMonth() });
  };

  const selectedItems = byDay[selected] || [];
  const todayKey = dayKey(today);
  const detailGroups = Object.values(
    selectedItems.reduce((acc, i) => {
      const comune = i.cemetery?.comune || "Senza comune";
      (acc[comune] = acc[comune] || { comune, items: [] }).items.push(i);
      return acc;
    }, {})
  ).sort((a, b) => a.comune.localeCompare(b.comune));

  return (
    <div className="fixed inset-0 z-[80] bg-[#f8fafc] overflow-y-auto" data-testid="pro-calendar-full" role="dialog" aria-modal="true">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 pb-16">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="eyebrow mb-1">Il mio calendario</p>
            <h1 className="font-serif text-3xl text-navy">{MONTHS[cursor.month]} {cursor.year}</h1>
          </div>
          <button
            data-testid="calendar-close"
            onClick={onClose}
            className="p-3 rounded-full border border-[#e2e8f0] bg-white text-navy hover:bg-[#f0f7ff] transition-colors duration-200"
            aria-label="Chiudi calendario"
          >
            <X size={22} />
          </button>
        </div>

        {items === null ? (
          <Loading />
        ) : (
          <>
            <div className="flex items-center justify-between mb-4">
              <button data-testid="cal-prev-month" onClick={() => move(-1)} className="p-3 rounded-full border border-[#dbe7f3] bg-white text-[#1e3a5f]" aria-label="Mese precedente">
                <ChevronLeft size={20} />
              </button>
              <p className="font-semibold text-navy" data-testid="cal-month-label">{MONTHS[cursor.month]} {cursor.year}</p>
              <button data-testid="cal-next-month" onClick={() => move(1)} className="p-3 rounded-full border border-[#dbe7f3] bg-white text-[#1e3a5f]" aria-label="Mese successivo">
                <ChevronRight size={20} />
              </button>
            </div>

            <div className="grid grid-cols-7 gap-1.5 mb-2">
              {WEEKDAYS.map((w, i) => (
                <p key={i} className="text-center text-xs font-bold text-muted-soft uppercase">{w}</p>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1.5" data-testid="cal-grid">
              {cells.map((key, idx) => {
                if (!key) return <div key={`blank-${idx}`} />;
                const count = (byDay[key] || []).length;
                const isSelected = key === selected;
                const isToday = key === todayKey;
                return (
                  <button
                    key={key}
                    data-testid={`cal-day-${key}`}
                    onClick={() => setSelected(key)}
                    className={`min-h-[64px] sm:min-h-[84px] rounded-xl border p-1.5 flex flex-col items-center justify-start gap-1 transition-colors duration-150 ${
                      count > 0 ? "bg-[#e8f0eb] border-[#4a7c59]" : "bg-white border-[#e2e8f0]"
                    } ${isSelected ? "ring-2 ring-[#1e3a5f]" : ""}`}
                  >
                    <span className={`text-base sm:text-lg font-semibold ${isToday ? "w-7 h-7 rounded-full bg-[#1e3a5f] text-white flex items-center justify-center" : "text-navy"}`}>
                      {parseInt(key.slice(-2), 10)}
                    </span>
                    {count > 0 && (
                      <span data-testid={`cal-count-${key}`} className="text-[11px] font-bold text-white bg-[#4a7c59] rounded-full px-2 py-0.5 leading-tight">
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="mt-8" data-testid="cal-day-detail">
              <p className="eyebrow mb-3">
                {new Date(`${selected}T12:00:00`).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
              </p>
              {selectedItems.length === 0 ? (
                <EmptyState title="Nessun intervento in questa data" />
              ) : (
                <div className="space-y-6">
                  {detailGroups.map((g) => (
                    <div key={g.comune} data-testid={`cal-comune-${g.comune}`}>
                      <p className="eyebrow mb-2">{g.comune} — {g.items.length} interventi</p>
                      <div className="space-y-3">
                        {g.items.map((i) => (
                          <Link key={i.id} to={`/area-pro/interventi/${i.id}`} data-testid={`cal-item-${i.id}`}
                            className="card-soft p-5 flex items-center justify-between gap-4 flex-wrap">
                            <div>
                              <p className="font-semibold text-navy">{fmtTime(i.scheduled_date)} — {i.customer_name}</p>
                              <p className="text-sm text-muted-soft">
                                {i.service} · {i.cemetery?.name}{i.grave ? ` · ${i.grave.deceased_name} ${i.grave.deceased_surname}` : ""}
                              </p>
                            </div>
                            <ProStatusBadge status={i.status} />
                          </Link>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
